const DEFAULT_IMS_CLIENT_ID = 'availability-tracker';
const DEFAULT_IMS_SCOPE = 'AdobeID,openid';
const DEFAULT_IMS_LOCALE = 'en_US';
const IMS_SCRIPT_ID = 'adobe-ims-script';

const IMS_SCRIPT_URLS = {
  prod: 'https://auth.services.adobe.com/imslib/imslib.min.js',
  stg1: 'https://auth-stg1.services.adobe.com/imslib/imslib.min.js'
} as const;

const IMS_PROFILE_URLS = {
  prod: 'https://ims-na1.adobelogin.com/ims/profile/v1',
  stg1: 'https://ims-na1-stg1.adobelogin.com/ims/profile/v1'
} as const;

let imsLoadPromise: Promise<AdobeIMSClient> | null = null;
let cachedProfile: AdobeIMSProfile | null = null;
let cachedAccessToken: string | null = null;

function extractAccessToken(tokenValue: unknown): string | null {
  if (!tokenValue) {
    return null;
  }

  if (typeof tokenValue === 'string') {
    return tokenValue.trim() || null;
  }

  if (typeof tokenValue !== 'object') {
    return null;
  }

  const tokenRecord = tokenValue as Record<string, unknown>;

  const candidate = tokenRecord['token'];

  if (typeof candidate === 'string' && candidate.trim()) {
    return candidate.trim();
  }
  

  return null;
}

function parseEmailAllowList(value?: string): Set<string> {
  return new Set(
    (value || '')
      .split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
  );
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  return new Error('Adobe IMS authentication failed.');
}

function getConfiguredEnvironment(): 'prod' | 'stg1' {
  return 'prod';
}

function getScriptUrl(): string {
  return (
    IMS_SCRIPT_URLS[getConfiguredEnvironment()]
  );
}

function isDebugEnabled(): boolean {
  return false;
}

function getTokenFromIms(): string | null {
  try {
    const token = extractAccessToken(window.adobeIMS?.getAccessToken?.());
    return token || cachedAccessToken;
  } catch (error) {
    console.error('Failed to read IMS access token.', error);
    return cachedAccessToken;
  }
}

function getImsClientId(): string {
  return DEFAULT_IMS_CLIENT_ID;
}

function isSignedInToIms(): boolean {
  try {
    if (typeof window.adobeIMS?.isSignedInUser === 'function') {
      return Boolean(window.adobeIMS.isSignedInUser());
    }

    return Boolean(getTokenFromIms());
  } catch (error) {
    console.error('Failed to determine IMS sign-in state.', error);
    return Boolean(getTokenFromIms());
  }
}

async function fetchImsProfile(accessToken: string): Promise<AdobeIMSProfile> {
  const response = await fetch(IMS_PROFILE_URLS[getConfiguredEnvironment()], {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
     
    }
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Profile call failed: ${response.status} ${errorBody}`);
  }

  return response.json() as Promise<AdobeIMSProfile>;
}

async function getProfileFromIms(): Promise<AdobeIMSProfile | null> {
  const accessToken = getTokenFromIms();
  const profileGetter = window.adobeIMS?.getProfile;

  if (!accessToken || !isSignedInToIms()) {
    return cachedProfile;
  }

  try {
    const profile = await fetchImsProfile(accessToken);
    cachedProfile = profile;
    return profile;
  } catch (error) {
    console.error('Failed to fetch IMS profile via /ims/profile/v1.', error);
  }

  if (!profileGetter) {
    return cachedProfile;
  }

  try {
    const result = profileGetter();

    if (result && typeof (result as PromiseLike<AdobeIMSProfile>).then === 'function') {
      const profile = await result;
      cachedProfile = profile;
      return profile;
    }

    cachedProfile = result || null;
    return cachedProfile;
  } catch (error) {
    console.error('Failed to read IMS profile.', error);
    return cachedProfile;
  }
}

function pickFirstString(
  source: Record<string, unknown> | null | undefined,
  keys: string[]
): string | null {
  if (!source) {
    return null;
  }

  for (const key of keys) {
    const value = source[key];

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function buildSession(profile: AdobeIMSProfile | null, accessToken: string | null): AdobeImsSession {
  const adminEmails = parseEmailAllowList("");
  const editorEmails = parseEmailAllowList("");
  const email =
    pickFirstString(profile, ['email'])?.toLowerCase() ||
    pickFirstString(profile, ['userId'])?.toLowerCase() ||
    null;
  const displayName =
    pickFirstString(profile, ['displayName', 'name', 'givenName']) ||
    email ||
    'Adobe user';
  const isSignedIn = Boolean(accessToken);
  const hasAllowLists = adminEmails.size > 0 || editorEmails.size > 0;

  let accessLevel: AdobeImsAccessLevel = 'anonymous';

  let canManageData = false;

  if (isSignedIn) {
    if (email && adminEmails.has(email)) {
      accessLevel = 'admin';
      canManageData = true;
    } else if (email && editorEmails.has(email)) {
      accessLevel = 'editor';
      canManageData = true;
    } else {
      // Until `useAdobeImsAuth` loads a `users` row, treat as unregistered (no dashboard data).
      accessLevel = 'unregistered';
      canManageData = false;
    }
  }

  return {
    accessLevel,
    accessToken,
    canManageData,
    isSignedIn,
    user: isSignedIn ?
    {
      displayName,
      email
    } :
    null
  };
}

function configureAdobeId() {
  window.adobeid = {
    client_id: getImsClientId(),
    scope: DEFAULT_IMS_SCOPE,
    locale: DEFAULT_IMS_LOCALE,
    environment: getConfiguredEnvironment(),
    useLocalStorage: false,
    autoValidateToken: true,
    debug: isDebugEnabled(),
    api_parameters: {
      authorize: {
        redirect_uri: window.location.origin + '/'
      }
    },
    onReady(appState: unknown) {
      // console.info('IMS ready', appState);
      void syncAdobeImsSession();
    },
    onAccessToken(tokenInfo: unknown) {
      const tokenRecord =
        tokenInfo && typeof tokenInfo === 'object' ?
        tokenInfo as Record<string, unknown> :
        null;
      const isGuestToken = tokenRecord?.isGuestToken === true;
      cachedAccessToken = extractAccessToken(tokenInfo);

      // console.info('IMS access token received', {
      //   hasToken: Boolean(cachedAccessToken),
      //   isGuestToken
      // });

      if (!isGuestToken && cachedAccessToken) {
        void syncAdobeImsSession();
      }
    },
    onProfile(profile: AdobeIMSProfile) {
      cachedProfile = profile;
      window.myAppAuth?.onLogin(buildSession(profile, getTokenFromIms()));
    },
    onError(error: unknown) {
      const normalizedError = normalizeError(error);
      console.error('IMS error', normalizedError);
      window.myAppAuth?.onAuthError?.(normalizedError);
    }
  };
}

function loadImsScript(scriptUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(
      IMS_SCRIPT_ID
    ) as HTMLScriptElement | null;

    const handleLoad = () => resolve();
    const handleError = () => reject(new Error('Unable to load the Adobe IMS library.'));

    if (existingScript) {
      if (window.adobeIMS) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = IMS_SCRIPT_ID;
    script.src = scriptUrl;
    script.async = true;
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });
}

export async function ensureAdobeIms(): Promise<AdobeIMSClient> {
  if (window.adobeIMS) {
    return window.adobeIMS;
  }

  if (!imsLoadPromise) {
    configureAdobeId();
    imsLoadPromise = loadImsScript(getScriptUrl()).then(() => {
      if (!window.adobeIMS) {
        throw new Error('Adobe IMS loaded without exposing the global client.');
      }

      return window.adobeIMS;
    });
  }

  return imsLoadPromise;
}

export async function syncAdobeImsSession(): Promise<AdobeImsSession> {
  await ensureAdobeIms();
  const [profile, accessToken] = await Promise.all([
    getProfileFromIms(),
    Promise.resolve(getTokenFromIms())
  ]);
  const session = buildSession(profile, accessToken);

  if (session.isSignedIn) {
    window.myAppAuth?.onLogin(session);
  } else {
    window.myAppAuth?.onLogout?.();
  }

  return session;
}

export function getAdobeAccessToken(): string | null {
  return getTokenFromIms();
}

export function getAuthenticatedUserId(): string | null {
  return (
    pickFirstString(cachedProfile, ['email','userId', 'sub']) ||
    null
  );
  // return "";
}

export async function signInWithAdobe(): Promise<void> {
  const ims = await ensureAdobeIms();
  ims.signIn();
}

export async function signOutFromAdobe(): Promise<void> {
  const ims = await ensureAdobeIms();
  cachedProfile = null;
  cachedAccessToken = null;
  ims.signOut();
  window.myAppAuth?.onLogout?.();
}
